import { ensureTypeInfo } from './NeoTypeInfo';
import { SerialiseType, RelationType, OnChangedOptions, DataType, TimeZoneFormat } from './Misc';
function getOrAddPropertyInfo(type, propertyName) {
    return ensureTypeInfo(type.constructor).neo.getOrAddProperty(propertyName);
}
export function Display(name) {
    return function (type, propertyKey) {
        var pi = getOrAddPropertyInfo(type, propertyKey);
        pi.displayName = name;
    };
}
/** Indicates that this is a date property. Json strings will be converted to date objects on deserialisation. */
export function Date(timeZoneSerialisation) {
    if (timeZoneSerialisation === void 0) { timeZoneSerialisation = TimeZoneFormat.UTC; }
    return function (type, propertyKey, descriptor) {
        var pi = getOrAddPropertyInfo(type, propertyKey);
        pi.propertyType = DataType.Date;
        pi.timeZoneSerialisation = timeZoneSerialisation;
    };
}
/** Indicates that this is an integer number property. Editors will be restricted to integer numeric input. */
export function Integer() {
    return function (type, propertyKey) {
        getOrAddPropertyInfo(type, propertyKey).propertyType = DataType.Int;
    };
}
/**
 * Indicates that this is a float number property.
 * Editors will be restricted to float numeric input.
 * @param decimals No of decimals allowed when storing this value. Used when binding to this property from a supporting ui component. If not specified, this will be determined from the format string. */
export function Float(decimals) {
    return function (type, propertyKey) {
        var propertyInfo = getOrAddPropertyInfo(type, propertyKey);
        propertyInfo.propertyType = DataType.Float;
        propertyInfo.allowedDecimals = decimals;
    };
}
/** Indicates that this is a decimal property (decimal type from the decimal.js-light package). */
export function Decimal() {
    return function (type, propertyKey) {
        getOrAddPropertyInfo(type, propertyKey).propertyType = DataType.Decimal;
    };
}
/** Indicates that this is a nullable boolean property. */
export function NullableBoolean() {
    return function (type, propertyKey) {
        getOrAddPropertyInfo(type, propertyKey).propertyType = DataType.NullableBoolean;
    };
}
/** Indicates that this property accepts nulls from user input controls that do not normally accept nulls. E.g. numeric editors. */
export function Nullable(dataType) {
    return function (type, propertyKey) {
        var pi = getOrAddPropertyInfo(type, propertyKey);
        pi.allowNulls = true;
        if (dataType) {
            pi.propertyType = dataType;
        }
    };
}
export function Observable(isObservable) {
    if (isObservable === void 0) { isObservable = true; }
    return function (type, propertyKey) {
        var pi = getOrAddPropertyInfo(type, propertyKey);
        pi.isObservable = isObservable;
    };
}
/** Indicates that changes to this property will not cause the object to become dirty. It will also not be serialised */
export function NoTracking(serialisation) {
    if (serialisation === void 0) { serialisation = SerialiseType.MappingOnly; }
    return function (type, propertyKey) {
        var pi = getOrAddPropertyInfo(type, propertyKey);
        pi.noTrack = true;
        // Don't overwrite if serialise has already been set.
        if (pi.serialise == SerialiseType.Always)
            pi.serialise = serialisation;
    };
}
export function Serialisation(serialisation) {
    return function (type, propertyKey) {
        var pi = getOrAddPropertyInfo(type, propertyKey);
        pi.serialise = serialisation;
    };
}
/** Indicates that this is a list property, and specifies the item type. This is not required if the list is initialised with a value. */
export function ChildList(childType) {
    return function (type, propertyKey) {
        var pi = getOrAddPropertyInfo(type, propertyKey);
        pi.setPropertyType(childType);
        if (pi.relationType === RelationType.None) {
            pi.relationType = RelationType.ChildList;
            pi.propertyType = DataType.Array;
            // Also add child properties to a dedicated list. (Used in graph iterator)
            type.constructor.neo.children.push(pi);
        }
    };
}
export function ChildObject(childType, isValueObject) {
    if (isValueObject === void 0) { isValueObject = false; }
    return function (type, propertyKey) {
        var pi = getOrAddPropertyInfo(type, propertyKey);
        if (childType === undefined) {
            throw Error("Unknown child type on ".concat(type.constructor.name, ".").concat(propertyKey, ", you may have a circular dependency. Try use @Attributes.ChildObject(() => Type)"));
        }
        pi.setPropertyType(childType);
        if (pi.relationType === RelationType.None) {
            pi.relationType = (isValueObject /*|| childType.prototype instanceof ValueObject* <- Causes circular dependency... */) ? RelationType.ValueObject : RelationType.ChildObject;
            pi.propertyType = DataType.Object;
            // Also add child properties to a dedicated list. (Used in graph iterator)
            type.constructor.neo.children.push(pi);
        }
    };
}
export function Dictionary(valueType) {
    return function (type, propertyKey) {
        var pi = getOrAddPropertyInfo(type, propertyKey);
        pi.setPropertyType(valueType);
        if (pi.relationType === RelationType.None) {
            pi.relationType = RelationType.Dictionary;
            pi.propertyType = DataType.Object;
        }
    };
}
export function Parent(parentType) {
    return function (type, propertyKey) {
        var pi = getOrAddPropertyInfo(type, propertyKey);
        pi.setPropertyType(parentType);
        pi.relationType = RelationType.Parent;
        pi.propertyType = DataType.Object;
        // Also add parent properties to a dedicated list (Used by List to set the parent property to the actual parent object)
        var typeInfo = type.constructor.neo;
        typeInfo.parentProperties.push(pi);
        if (!typeInfo.parentProperty) {
            typeInfo.parentProperty = pi;
        }
    };
}
/**
 * Indicates that this property must be assigned to on deserialisation. The deserialiser will not try create a model instance of the object / array.
 */
export function Raw() {
    return function (type, propertyKey) {
        var pi = getOrAddPropertyInfo(type, propertyKey);
        pi.assignOnDeserialise = true;
    };
}
/** Registers an onChanged event handler for changes to this property
 * @param requiresOldValue Set to true if this property is not tracked and you need the old value.
 * @param delayMs Number in milliseconds that can be used to debounce the callback function
*/
export function OnChanged(callback, requiresOldValue, delayMs) {
    if (requiresOldValue === void 0) { requiresOldValue = false; }
    if (delayMs === void 0) { delayMs = 0; }
    return function (type, propertyKey) {
        var pi = getOrAddPropertyInfo(type, propertyKey);
        pi.onChangedOptions = new OnChangedOptions(callback(type), requiresOldValue, delayMs);
    };
}
/**
 * Set if you want this value to be debounced when calling toQueryObjectAuto, or using the AutoRunner service.
 * By default, strings or properties decorated with integer or float are debounced.
 * @param value Override the default value.
 */
export function Debounce(value) {
    return function (type, propertyKey) {
        var pi = getOrAddPropertyInfo(type, propertyKey);
        pi.debounce = value !== false;
    };
}
/** Converts a getter into a computed. Note, when using NeoModel you do not need to do this. */
export function Computed(isComputed) {
    if (isComputed === void 0) { isComputed = true; }
    return function (type, propertyKey, descriptor) {
        ensureTypeInfo(type.constructor).neo.addComputed(propertyKey, descriptor, isComputed);
    };
}
/** Specifies whether a method is a mobx action.
 * Change the default behaviour at a class level in the makeObservable options passed to makeObservable. */
export function Action(isAction) {
    if (isAction === void 0) { isAction = true; }
    return function (type, propertyKey, descriptor) {
        ensureTypeInfo(type.constructor).neo.addMethod(propertyKey).isAction = isAction;
    };
}
/** Sets this property as the display member to be used in drop downs / radio lists etc. */
export function DisplayMember() {
    return function (type, propertyKey) {
        var pi = getOrAddPropertyInfo(type, propertyKey);
        ensureTypeInfo(type.constructor).neo.displayMember = pi;
    };
}
